import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Input, Select, Tabs, Tooltip, Typography } from 'antd';
import {
  CodeOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  SaveOutlined,
  SendOutlined,
} from '@ant-design/icons';
import type {
  ApiRequest,
  HttpMethod,
  RequestHeader,
  RequestParam,
  RequestVariable,
} from '@core/entities/ApiRequest';
import { container } from '@infrastructure/di/container';
import {
  buildDocumentPayload,
  buildHeaderMap,
  buildVariableMap,
  flattenResponseHeaders,
  mergeVariableChanges,
} from '@presentation/utils/apiRequestPayload';
import { QUERY_KEYS, STORAGE_KEYS } from '@shared/constants';
import { useWorkbenchStore, type WorkbenchTab } from '@presentation/stores/workbenchStore';
import { useResponseStore } from '@presentation/stores/responseStore';
import { useThemeStore } from '@presentation/stores/themeStore';
import { useConsoleStore } from '@presentation/stores/consoleStore';
import { useTestResultsStore } from '@presentation/stores/testResultsStore';
import { runScript } from '@presentation/services/scriptSandbox';
import { syncParamsFromUrl, syncUrlFromParams } from '@presentation/utils/urlParamsSync';
import { useParseCurl } from '@presentation/hooks/useApiRequests';
import { KeyValueRows } from '@presentation/components/workbench/KeyValueRows';
import { BulkEditableKeyValueRows } from '@presentation/components/workbench/BulkEditableKeyValueRows';
import { AuthEditor } from '@presentation/components/workbench/AuthEditor';
import { BodyEditor } from '@presentation/components/workbench/BodyEditor';
import { ResponseViewer } from '@presentation/components/workbench/ResponseViewer';
import { ResizableSplit } from '@presentation/components/workbench/ResizableSplit';
import { GenerateCodeModal } from '@presentation/components/workbench/GenerateCodeModal';
import { MarkdownEditor } from '@presentation/components/workbench/MarkdownEditor';
import { ScriptEditor } from '@presentation/components/workbench/ScriptEditor';
import { SettingsPanel } from '@presentation/components/workbench/SettingsPanel';
import { ExamplesPanel } from '@presentation/components/workbench/ExamplesPanel';

const METHOD_OPTIONS: { label: string; value: HttpMethod }[] = [
  'GET',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
  'OPTIONS',
  'HEAD',
  'TRACE',
  'CONNECT',
].map((method) => ({ label: method, value: method as HttpMethod }));

const createEmptyParam = (): RequestParam => ({
  id: crypto.randomUUID(),
  type: 'query',
  key: '',
  value: '',
  description: '',
  isEnabled: true,
  position: 0,
});

const createEmptyHeader = (): RequestHeader => ({
  id: crypto.randomUUID(),
  key: '',
  value: '',
  description: '',
  isEnabled: true,
  position: 0,
});

const createEmptyVariable = (): RequestVariable => ({
  id: crypto.randomUUID(),
  key: '',
  value: '',
  type: 'default',
  isEnabled: true,
  position: 0,
});

const readStoredResponseHidden = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEYS.apiTesterResponseHidden) === '1';
  } catch {
    return false;
  }
};

const persistResponseHidden = (hidden: boolean): void => {
  try {
    window.localStorage.setItem(STORAGE_KEYS.apiTesterResponseHidden, hidden ? '1' : '0');
  } catch {
    // Private browsing / quota exceeded — the toggle still works this session, it just won't persist.
  }
};

interface RequestBuilderPanelProps {
  tab: WorkbenchTab;
}

export const RequestBuilderPanel = ({ tab }: RequestBuilderPanelProps) => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const themeMode = useThemeStore((state) => state.mode);
  const updateDraft = useWorkbenchStore((state) => state.updateDraft);
  const markSaved = useWorkbenchStore((state) => state.markSaved);
  const response = useResponseStore((state) => state.responses[tab.key]);
  const isSending = useResponseStore((state) => state.sending[tab.key] ?? false);
  const setResponse = useResponseStore((state) => state.setResponse);
  const setSending = useResponseStore((state) => state.setSending);
  // Hidden persists across reloads (a deliberate workspace-layout choice);
  // fullscreen is a momentary focus mode and resets on reload like most
  // editors' zen/fullscreen toggles do.
  const [responseHidden, setResponseHidden] = useState(readStoredResponseHidden);
  const [responseFullscreen, setResponseFullscreen] = useState(false);
  const [scriptFullscreen, setScriptFullscreen] = useState<'pre' | 'test' | null>(null);

  const toggleResponseHidden = (hidden: boolean) => {
    setResponseHidden(hidden);
    persistResponseHidden(hidden);
    if (hidden) setResponseFullscreen(false);
  };
  const addLog = useConsoleStore((state) => state.add);
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const parseCurl = useParseCurl();
  const setTestResults = useTestResultsStore((state) => state.setResults);

  const invalidateTree = (request: ApiRequest) => {
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.apiRequests(request.collectionId, request.folderId),
    });
  };

  const saveMutation = useMutation({
    mutationFn: async (): Promise<ApiRequest> => {
      if (tab.requestId) {
        return container.apiRequests.update(tab.requestId, buildDocumentPayload(tab.draft));
      }
      return container.apiRequests.create(tab.collectionId, {
        ...buildDocumentPayload(tab.draft),
        name: tab.draft.name,
        method: tab.draft.method,
        url: tab.draft.url,
        folderId: tab.folderId ?? undefined,
      });
    },
    onSuccess: (request) => {
      markSaved(tab.key, request);
      invalidateTree(request);
      void message.success('Saved');
      addLog({ level: 'info', message: `Saved "${request.name}"`, source: 'Request' });
    },
    onError: () => void message.error('Failed to save request'),
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      setSending(tab.key, true);

      const requestContext = {
        method: tab.draft.method,
        url: tab.draft.url,
        headers: buildHeaderMap(tab.draft.headers),
      };
      let variableChanges: Record<string, string> = {};

      if (tab.draft.preRequestScript?.trim()) {
        const preResult = runScript({
          script: tab.draft.preRequestScript,
          request: requestContext,
          variables: buildVariableMap(tab.draft.variables),
        });
        preResult.logs.forEach((log) =>
          addLog({ level: log.level, message: log.message, source: 'Pre-request Script' }),
        );
        if (preResult.error) {
          addLog({ level: 'error', message: preResult.error, source: 'Pre-request Script' });
          throw new Error(`Pre-request script error: ${preResult.error}`);
        }
        variableChanges = preResult.variableChanges;
      }

      addLog({ level: 'info', message: `${tab.draft.method} ${tab.draft.url}`, source: 'Request' });

      let requestId = tab.requestId;
      let overridesNeeded = Object.keys(variableChanges).length > 0;

      if (!requestId) {
        const saved = await container.apiRequests.create(tab.collectionId, {
          ...buildDocumentPayload(tab.draft),
          name: tab.draft.name,
          method: tab.draft.method,
          url: tab.draft.url,
          folderId: tab.folderId ?? undefined,
        });
        requestId = saved.id;
        markSaved(tab.key, saved);
        invalidateTree(saved);
      } else {
        overridesNeeded = true;
      }

      const overrides = overridesNeeded
        ? mergeVariableChanges(buildDocumentPayload(tab.draft), variableChanges)
        : undefined;
      const entry = await container.apiRequests.send(requestId, overrides);

      if (tab.draft.testScript?.trim()) {
        const responseContext = entry.response
          ? {
              code: entry.statusCode ?? 0,
              status: entry.response.statusText ?? '',
              responseTimeMs: entry.durationMs ?? 0,
              headers: flattenResponseHeaders(entry.response.headers),
              body: entry.response.body ?? '',
            }
          : undefined;

        const testResult = runScript({
          script: tab.draft.testScript,
          request: requestContext,
          variables: { ...buildVariableMap(tab.draft.variables), ...variableChanges },
          response: responseContext,
        });
        testResult.logs.forEach((log) =>
          addLog({ level: log.level, message: log.message, source: 'Test Script' }),
        );
        if (testResult.error)
          addLog({ level: 'error', message: testResult.error, source: 'Test Script' });
        setTestResults(tab.key, { tests: testResult.tests, scriptError: testResult.error });
      } else {
        setTestResults(tab.key, { tests: [], scriptError: null });
      }

      return entry;
    },
    onSuccess: (entry) => {
      setResponse(tab.key, entry);
      addLog({
        level: entry.errorMessage ? 'error' : 'info',
        message: entry.errorMessage
          ? `Failed: ${entry.errorMessage}`
          : `${entry.statusCode} ${entry.response?.statusText ?? ''} — ${entry.durationMs ?? 0} ms`,
        source: 'Response',
      });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.history(tab.workspaceId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.cookies(tab.workspaceId) });
    },
    onError: (error: Error) => {
      void message.error(error.message || 'Failed to send request');
      addLog({
        level: 'error',
        message: error.message || 'Failed to send request',
        source: 'Request',
      });
    },
    onSettled: () => setSending(tab.key, false),
  });

  const canSend = tab.draft.url.trim().length > 0;

  const handleUrlPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text').trim();
    if (!/^\$?\s*curl\s/i.test(pasted)) return;

    event.preventDefault();
    parseCurl.mutate(pasted, {
      onSuccess: (parsed) => {
        updateDraft(tab.key, {
          name: tab.draft.name === 'Untitled Request' ? parsed.name : tab.draft.name,
          method: parsed.method,
          url: parsed.url,
          authType: parsed.authType,
          authConfig: parsed.authConfig,
          headers: parsed.headers.map((header) => ({
            id: crypto.randomUUID(),
            key: header.key,
            value: header.value ?? '',
            description: header.description ?? '',
            isEnabled: header.isEnabled ?? true,
            position: 0,
          })),
          body: parsed.body
            ? {
                mode: parsed.body.mode,
                rawFormat: parsed.body.rawFormat ?? null,
                rawContent: parsed.body.rawContent ?? null,
                graphqlQuery: parsed.body.graphqlQuery ?? null,
                graphqlVariables: parsed.body.graphqlVariables ?? null,
                binaryFilePath: parsed.body.binaryFilePath ?? null,
                fields: (parsed.body.fields ?? []).map((field) => ({
                  id: crypto.randomUUID(),
                  key: field.key,
                  value: field.value ?? '',
                  type: field.type ?? 'text',
                  filePath: field.filePath ?? null,
                  isEnabled: field.isEnabled ?? true,
                  position: 0,
                })),
              }
            : null,
        });
        void message.success(
          'cURL command parsed — method, URL, headers, body, and auth filled in.',
        );
      },
      onError: (error: Error) =>
        void message.error(error.message || "That doesn't look like a valid cURL command"),
    });
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;

      if (event.key === 'Enter') {
        event.preventDefault();
        if (canSend && !sendMutation.isPending) sendMutation.mutate();
      } else if (event.key.toLowerCase() === 's') {
        event.preventDefault();
        if (!saveMutation.isPending) saveMutation.mutate();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canSend, sendMutation, saveMutation]);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex items-center gap-2 border-b border-black/10 p-2 dark:border-white/10">
        <Select
          size="middle"
          value={tab.draft.method}
          options={METHOD_OPTIONS}
          onChange={(method: HttpMethod) => updateDraft(tab.key, { method })}
          className="w-28"
        />
        <Input
          size="middle"
          placeholder="https://api.example.com/users?{{query}} (paste a cURL command to auto-fill)"
          value={tab.draft.url}
          onChange={(event) => {
            const url = event.target.value;
            updateDraft(tab.key, { url, params: syncParamsFromUrl(url, tab.draft.params) });
          }}
          onPressEnter={() => canSend && sendMutation.mutate()}
          onPaste={handleUrlPaste}
          className="flex-1 font-mono"
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          loading={sendMutation.isPending}
          disabled={!canSend}
          onClick={() => sendMutation.mutate()}
        >
          Send
        </Button>
        <Button
          icon={<SaveOutlined />}
          loading={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          Save
        </Button>
        <Button icon={<CodeOutlined />} onClick={() => setCodeModalOpen(true)}>
          Code
        </Button>
      </div>

      <GenerateCodeModal
        open={codeModalOpen}
        onClose={() => setCodeModalOpen(false)}
        request={{
          method: tab.draft.method,
          url: tab.draft.url,
          params: tab.draft.params.map((param) => ({
            type: param.type,
            key: param.key,
            value: param.value ?? '',
            enabled: param.isEnabled,
          })),
          headers: tab.draft.headers.map((header) => ({
            key: header.key,
            value: header.value ?? '',
            enabled: header.isEnabled,
          })),
          authType: tab.draft.authType,
          authConfig: tab.draft.authConfig,
          body: tab.draft.body
            ? {
                mode: tab.draft.body.mode,
                rawContent: tab.draft.body.rawContent,
                rawFormat: tab.draft.body.rawFormat,
                fields: tab.draft.body.fields.map((field) => ({
                  key: field.key,
                  value: field.value ?? '',
                  type: field.type,
                  enabled: field.isEnabled,
                })),
                graphqlQuery: tab.draft.body.graphqlQuery,
                graphqlVariables: tab.draft.body.graphqlVariables,
              }
            : null,
        }}
      />

      <Input
        size="small"
        value={tab.draft.name}
        onChange={(event) => updateDraft(tab.key, { name: event.target.value })}
        variant="borderless"
        placeholder="Request name"
        className="border-b border-black/10 px-2 !text-xs text-gray-500 dark:border-white/10"
      />

      <ResizableSplit
        storageKey={STORAGE_KEYS.apiTesterRequestPanelSize}
        bottomHidden={responseHidden}
        topHidden={responseFullscreen}
        top={
          <Tabs
            size="small"
            className="h-full min-h-0 px-2 [&_.ant-tabs-body]:h-full [&_.ant-tabs-body-holder]:h-full [&_.ant-tabs-content]:h-full [&_.ant-tabs-nav]:!mb-0 [&_.ant-tabs-tabpane]:h-full"
            items={[
              {
                key: 'params',
                label: `Params${tab.draft.params.length ? ` (${tab.draft.params.length})` : ''}`,
                children: (
                  <div className="h-full overflow-auto p-2">
                    <BulkEditableKeyValueRows
                      rows={tab.draft.params}
                      onChange={(params) =>
                        updateDraft(tab.key, {
                          params,
                          url: syncUrlFromParams(tab.draft.url, params),
                        })
                      }
                      typeOptions={[
                        { label: 'Query', value: 'query' },
                        { label: 'Path', value: 'path' },
                      ]}
                      showDescription
                      createRow={createEmptyParam}
                    />
                  </div>
                ),
              },
              {
                key: 'headers',
                label: `Headers${tab.draft.headers.length ? ` (${tab.draft.headers.length})` : ''}`,
                children: (
                  <div className="h-full overflow-auto p-2">
                    <BulkEditableKeyValueRows
                      rows={tab.draft.headers}
                      onChange={(headers) => updateDraft(tab.key, { headers })}
                      showDescription
                      createRow={createEmptyHeader}
                    />
                  </div>
                ),
              },
              {
                key: 'auth',
                label: 'Auth',
                children: (
                  <div className="h-full overflow-auto p-2">
                    <AuthEditor
                      authType={tab.draft.authType}
                      authConfig={tab.draft.authConfig}
                      onChange={(authType, authConfig) =>
                        updateDraft(tab.key, { authType, authConfig })
                      }
                    />
                  </div>
                ),
              },
              {
                key: 'body',
                label: 'Body',
                children: (
                  <div className="h-full overflow-auto p-2">
                    <BodyEditor
                      body={tab.draft.body}
                      onChange={(body) => updateDraft(tab.key, { body })}
                      requestUrl={tab.draft.url}
                      requestHeaders={buildHeaderMap(tab.draft.headers)}
                    />
                  </div>
                ),
              },
              {
                key: 'variables',
                label: `Variables${tab.draft.variables.length ? ` (${tab.draft.variables.length})` : ''}`,
                children: (
                  <div className="h-full overflow-auto p-2">
                    <KeyValueRows
                      rows={tab.draft.variables}
                      onChange={(variables) => updateDraft(tab.key, { variables })}
                      typeOptions={[
                        { label: 'Default', value: 'default' },
                        { label: 'Secret', value: 'secret' },
                      ]}
                      createRow={createEmptyVariable}
                    />
                  </div>
                ),
              },
              {
                key: 'scripts',
                label: 'Scripts',
                children: (
                  <div className="flex h-full flex-col gap-2 overflow-hidden p-2">
                    {scriptFullscreen === null && (
                      <Typography.Text type="secondary" className="text-xs">
                        Runs in the browser on Send. Available: <code>pm.test(name, fn)</code>,{' '}
                        <code>pm.expect(value)</code>, <code>pm.environment.get/set(key)</code>,{' '}
                        <code>pm.request</code>, <code>pm.response.code/json()/text()/headers</code>
                        , <code>console.log</code> (see the Console panel).
                      </Typography.Text>
                    )}
                    <ScriptEditor
                      label="Pre-request script"
                      value={tab.draft.preRequestScript ?? ''}
                      onChange={(value) => updateDraft(tab.key, { preRequestScript: value })}
                      themeMode={themeMode}
                      isFullscreen={scriptFullscreen === 'pre'}
                      onToggleFullscreen={() =>
                        setScriptFullscreen((current) => (current === 'pre' ? null : 'pre'))
                      }
                      hidden={scriptFullscreen === 'test'}
                    />
                    <ScriptEditor
                      label="Test script"
                      value={tab.draft.testScript ?? ''}
                      onChange={(value) => updateDraft(tab.key, { testScript: value })}
                      themeMode={themeMode}
                      isFullscreen={scriptFullscreen === 'test'}
                      onToggleFullscreen={() =>
                        setScriptFullscreen((current) => (current === 'test' ? null : 'test'))
                      }
                      hidden={scriptFullscreen === 'pre'}
                    />
                  </div>
                ),
              },
              {
                key: 'settings',
                label: 'Settings',
                children: (
                  <div className="h-full overflow-auto p-2">
                    <SettingsPanel
                      workspaceId={tab.workspaceId}
                      settings={tab.draft.settings}
                      onChange={(settings) => updateDraft(tab.key, { settings })}
                    />
                  </div>
                ),
              },
              {
                key: 'docs',
                label: 'Docs',
                children: (
                  <div className="h-full overflow-auto p-2">
                    <MarkdownEditor
                      value={tab.draft.description ?? ''}
                      onChange={(description) => updateDraft(tab.key, { description })}
                      placeholder="Document what this request does, its parameters, and example responses…"
                    />
                  </div>
                ),
              },
              {
                key: 'examples',
                label: 'Examples',
                children: <ExamplesPanel requestId={tab.requestId} />,
              },
            ]}
          />
        }
        bottom={
          <div className="flex h-full flex-col border-t border-black/10 dark:border-white/10">
            <div className="flex shrink-0 items-center justify-end gap-1 border-b border-black/10 px-2 py-1 dark:border-white/10">
              {responseFullscreen ? (
                <Tooltip title="Restore split view">
                  <Button
                    size="small"
                    type="text"
                    icon={<FullscreenExitOutlined />}
                    aria-label="Restore response panel"
                    onClick={() => setResponseFullscreen(false)}
                  />
                </Tooltip>
              ) : (
                <>
                  <Tooltip title="Fullscreen response">
                    <Button
                      size="small"
                      type="text"
                      icon={<FullscreenOutlined />}
                      aria-label="Fullscreen response"
                      onClick={() => {
                        setResponseFullscreen(true);
                        if (responseHidden) toggleResponseHidden(false);
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Hide response">
                    <Button
                      size="small"
                      type="text"
                      icon={<EyeInvisibleOutlined />}
                      aria-label="Hide response"
                      onClick={() => toggleResponseHidden(true)}
                    />
                  </Tooltip>
                </>
              )}
            </div>
            <div className="min-h-0 flex-1">
              <ResponseViewer
                entry={response}
                isSending={isSending}
                tabKey={tab.key}
                requestId={tab.requestId}
              />
            </div>
          </div>
        }
      />

      {responseHidden && !responseFullscreen && (
        <div className="flex shrink-0 items-center justify-between border-t border-black/10 px-3 py-1.5 text-xs text-gray-500 dark:border-white/10">
          <span>Response hidden</span>
          <Button
            size="small"
            type="link"
            icon={<EyeOutlined />}
            aria-label="Show response"
            onClick={() => toggleResponseHidden(false)}
          >
            Show Response
          </Button>
        </div>
      )}
    </div>
  );
};
